import ConfigurationHeader from 'modules/configuration/components/configuration-header/ConfigurationHeader';
import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    ACTION_ITEMS,
    DELETE_POPUP_SUMMARY_PART1,
    DELETE_POPUP_SUMMARY_PART2,
    DELETE_POPUP_TITLE,
    MAIN_ROUTES,
    WHATS_NEW_GUIDE
} from 'common/config';
import { DataList, MessageGuide, MessageModal } from 'common/components';
import WhatsNewLinkModal from 'modules/configuration/components/whats-new-link-modal/WhatsNewLinkModal';
import { WHATS_NEW_TITLE, getWhatsNewColDefs } from 'modules/configuration/config';
import { Action, ApiStatus, ButtonLabel, EntityActions } from 'common/enums';
import { WhatsNewLinkType } from 'modules/configuration/enums/configuration.enum';
import { configurationActions } from 'modules/configuration/redux';
import { RootState } from 'common/redux/core/root.reducer';
import {
    getConfigurationDeleteResponse,
    getConfigurationReorderResponse,
    getWhatsNewResponse
} from 'modules/configuration/redux/configuration.selector';
import {
    ConfigurationReorderUpdateList,
    ConfigurationWhatsNewDeleteRequest
} from 'modules/configuration/models/configuration.model';
import useWhatsNewAccess from 'modules/configuration/roles-access-hooks/useWhatsNewAccess';

const WhatsNew: FC = () => {
    /**
     * State variable to contain search text
     */
    const [searchVal, setSearchVal] = useState<string>('');
    const [popupDetails, setPopupDetails] = useState({ title: '', text: '', type: '' });
    const [modalData, setModalData] = useState<any>(null);
    const [isShowPopup, setShowPopup] = useState<boolean>(false);
    const [showLinkModal, setShowLinkModal] = useState<boolean>(false);
    const [linkModalData, setLinkModalData] = useState<any>(null);
    const { isSetUpDisabled } = useWhatsNewAccess();

    const whatsNewResponse = useSelector((state: RootState) => getWhatsNewResponse(state));
    const indicatorDeleteResponse = useSelector((state: RootState) =>
        getConfigurationDeleteResponse(state)
    );
    const indicatorReorderResponse = useSelector((state: RootState) =>
        getConfigurationReorderResponse(state)
    );

    const isIndicatorDeleteSuccess = indicatorDeleteResponse?.status === ApiStatus.SUCCESS;
    const isIndicatorReorderSuccess = indicatorReorderResponse?.status === ApiStatus.SUCCESS;
    const dispatch = useDispatch();
    const navigate = useNavigate();

    useEffect(() => {
        fetchWhatsNewList();
    }, []);

    useEffect(() => {
        if (isIndicatorDeleteSuccess) {
            fetchWhatsNewList();
            return () => {
                dispatch(configurationActions.resetDeleteData());
            };
        }
    }, [isIndicatorDeleteSuccess]);

    useEffect(() => {
        if (isIndicatorReorderSuccess) {
            fetchWhatsNewList();
            return () => {
                dispatch(configurationActions.resetReOrderData());
            };
        }
    }, [isIndicatorReorderSuccess]);

    const fetchWhatsNewList = () => {
        dispatch(configurationActions.fetchWhatsNew());
    };

    /**
     * This function handle serach event
     * @param event Event information object created by user on search box
     */

    const onSearchChangeHandler = (event: any) => {
        setSearchVal(event.target.value);
    };

    const handleDelete = (data: any) => {
        setPopupDetails({
            title: DELETE_POPUP_TITLE,
            text: `${DELETE_POPUP_SUMMARY_PART1} this record. ${DELETE_POPUP_SUMMARY_PART2}`,
            type: EntityActions.Delete
        });
        setModalData(data);
        setShowPopup(true);
    };

    const handleAction = (action: any, data: any) => {
        switch (action) {
            case Action.Edit: {
                navigate(MAIN_ROUTES.WHATS_NEW_CREATE_NEW, {
                    state: {
                        data: data
                    }
                });
                break;
            }
            case Action.Delete: {
                handleDelete(data);
                break;
            }
        }
    };

    const handleLink = (data: any) => {
        switch (data?.whatsNewTypeId) {
            case WhatsNewLinkType.ArticleLink:
            case WhatsNewLinkType.ApplicationLink:
                return window.open(data?.link, '_blank');
            case WhatsNewLinkType.Image:
            case WhatsNewLinkType.Video:
            case WhatsNewLinkType.Indicator:
                return handleLinkModal(data);
        }
    };

    const handleLinkModal = (data: any) => {
        setLinkModalData(data);
        setShowLinkModal(true);
    };

    const closeLinkModal = () => {
        setLinkModalData(null);
        setShowLinkModal(false);
    };

    const popupHandler = (modalData: any) => {
        if (popupDetails.type == EntityActions.Delete) {
            const apiPayload: ConfigurationWhatsNewDeleteRequest = {
                whatsNewTypes: modalData.whatsNewTypeId,
                id: modalData.id
            };
            dispatch(configurationActions.deleteWhatsNewData(apiPayload));
        }
        setShowPopup(false);
    };

    const getData = () => {
        if (searchVal.length > 0) {
            return whatsNewResponse.data?.filter((item: any) =>
                item.name.toLowerCase().includes(searchVal)
            );
        }
        return whatsNewResponse.data || [];
    };

    const onRowDragEnd = (e: any) => {
        if (whatsNewResponse.data && whatsNewResponse.data?.length > 1) {
            const reOrderUpdateList: ConfigurationReorderUpdateList[] = [];
            e.api.forEachNode((node: any) => {
                reOrderUpdateList.push({
                    order: node.rowIndex + 1,
                    id: node.data.id
                });
            });
            dispatch(configurationActions.reOrderWhatsNew(reOrderUpdateList));
        }
    };

    return (
        <div className="route-container">
            <ConfigurationHeader
                headerTitle={WHATS_NEW_TITLE}
                buttonTitle={ACTION_ITEMS.ADD_NEW_BUTTON}
                searchVal={searchVal}
                disableButton={
                    isSetUpDisabled ||
                    (whatsNewResponse &&
                        whatsNewResponse.data &&
                        whatsNewResponse?.data.length >= 10)
                }
                isButtonVisible={true}
                onSearchChangeHandler={onSearchChangeHandler}
                onAddNew={() => {
                    navigate(MAIN_ROUTES.WHATS_NEW_CREATE_NEW);
                }}
            />
            <MessageGuide message={WHATS_NEW_GUIDE} />
            <DataList
                isNoDataVisible={getData()?.length === 0}
                data={getData()}
                columnDefs={getWhatsNewColDefs(handleAction, handleLink, isSetUpDisabled)}
                onRowDragEnd={onRowDragEnd}
                suppressDrag={searchVal.length > 0}
            />
            <MessageModal
                headerName={popupDetails.title}
                message={popupDetails.text}
                isVisible={isShowPopup}
                isSecondaryButtonVisible={true}
                primaryButton={ButtonLabel.Ok}
                secondaryButton={ButtonLabel.Cancel}
                onPrimaryAction={() => {
                    popupHandler(modalData);
                }}
                onSecondaryAction={() => {
                    setShowPopup(false);
                }}
            />
            {showLinkModal && (
                <WhatsNewLinkModal
                    show={showLinkModal}
                    data={linkModalData}
                    onClose={closeLinkModal}
                />
            )}
        </div>
    );
};

export default WhatsNew;
